'use client'

import { useQueryClient } from '@tanstack/react-query'
import {
    AnimatePresence,
    animate,
    motion,
    useMotionValue,
    useTransform,
    useWillChange,
} from 'motion/react'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMeasure } from 'react-use'
import { toast } from 'sonner'
import { getCitiesQueryOptions } from '@/api/cities/get-cities-query'
import { ICity } from '@/api/cities/types/cities-types'
import { Input } from '@/components/input/input'
import { LoadingSpinner } from '@/components/loading-spinner'
import { CityOption } from '@/features/cities/city-option'
import { IOption } from '@/typings/option'
import { debounce } from '@/utils/functions/debounce'
import { run } from '@/utils/run'

export type ICityOption = IOption<ICity>

interface ICitySearchProps {
    label?: string
    placeholder?: string
    onSelect: (cityOption: ICityOption | null) => void
    showOptions?: boolean
    autoFocus?: boolean
}

const MAX_SEARCH_RESULTS = 5

export const CitySearch = ({
    label,
    onSelect: propsOnSelect,
    placeholder,
    showOptions = true,
    autoFocus,
}: ICitySearchProps) => {
    const t = useTranslations()
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(0)

    const [isLoading, setIsLoading] = useState(false)

    const qc = useQueryClient()
    const willChange = useWillChange()

    const [inputValue, setInputValue] = useState('')

    const [cityOptions, setCityOptions] = useState<ICityOption[] | null>(null)

    const resultsOpacity = useMotionValue(0)
    const loadingSpinnerOpacity = useTransform(resultsOpacity, [0, 1], [1, 0])

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            // Handle enter press when an option is highlighted
            if (event.key === 'Enter' && highlightedIndex !== null) {
                const selectedOption = cityOptions?.[highlightedIndex]

                if (selectedOption) propsOnSelect(selectedOption)
            }

            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault()

                const optionsCount = Math.min(cityOptions?.length || 0, MAX_SEARCH_RESULTS)
                if (optionsCount === 0) return

                let newIndex: number | null = null

                if (highlightedIndex === null) {
                    newIndex = event.key === 'ArrowUp' ? optionsCount - 1 : 0
                } else {
                    newIndex =
                        event.key === 'ArrowUp'
                            ? (highlightedIndex - 1 + optionsCount) % optionsCount
                            : (highlightedIndex + 1) % optionsCount
                }

                setHighlightedIndex(newIndex)
            }
        },
        [cityOptions, highlightedIndex, propsOnSelect]
    )

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [handleKeyDown])

    const locale = useLocale()

    const fetchOptions = useCallback(
        async (nextSearchValue: string) => {
            try {
                const response = await qc.fetchQuery(
                    getCitiesQueryOptions({ name: nextSearchValue }, locale)
                )

                const resultCities = response.data

                const isEmpty = !resultCities.length

                animate(resultsOpacity, 1, { duration: 0.07, ease: 'easeOut' })

                const freshOptions = resultCities.map((city) => ({
                    value: city,
                    label: city.name,
                }))

                setCityOptions(isEmpty ? [] : freshOptions)
            } catch (_error) {
                toast.error('shared.error.failed')
            }
        },
        [qc, resultsOpacity, locale]
    )

    const debouncedFetch = useMemo(
        () => debounce((nextSearchValue: string) => fetchOptions(nextSearchValue), 150),
        [fetchOptions]
    )

    const onValueChange = useCallback(
        (nextValue: string) => {
            setInputValue(nextValue)
            animate(resultsOpacity, nextValue ? 0.2 : 0)

            if (highlightedIndex !== null) setHighlightedIndex(0)

            if (!nextValue) {
                setCityOptions(null)
                return
            }

            const isFirstInput = inputValue === '' && nextValue.length === 1

            // When the user starts typing, we want to fetch the options immediately
            // so that the user gets direct feedback on the search and realizes, that the search is working
            // without a need to commit the input value by pressing enter or any other key
            if (isFirstInput) {
                fetchOptions(nextValue)
            } else {
                debouncedFetch(nextValue)
            }
        },
        [debouncedFetch, resultsOpacity, highlightedIndex, inputValue, fetchOptions]
    )

    const onSelect = useCallback(
        (selectedOption: ICityOption | null) => {
            propsOnSelect(selectedOption)
        },
        [propsOnSelect]
    )

    const containerRef = useRef<HTMLDivElement>(null)

    const [listRef, { height: listHeight }] = useMeasure<HTMLDivElement>()

    return (
        <div ref={containerRef} className="relative w-full md:w-auto ">
            <Input
                autoFocus={autoFocus}
                label={label}
                value={inputValue}
                onValueChange={onValueChange}
                placeholder={placeholder}
                styleType="grey"
                type="search"
                isLoading={isLoading}
                shape="pill"
            />

            <AnimatePresence>
                {Boolean(cityOptions) && Boolean(inputValue) && showOptions && (
                    <motion.div
                        style={{ willChange }}
                        initial={{
                            scale: 1,
                            height: 0,
                        }}
                        animate={{
                            scale: 1,
                            height: listHeight || undefined,
                        }}
                        exit={{
                            opacity: 0,
                        }}
                        transition={{
                            opacity: { duration: 0.05, ease: 'easeInOut' },
                            scale: { type: 'spring', stiffness: 1000, damping: 70 },
                            height: {
                                duration: 0.4,
                                type: 'spring',
                                stiffness: 400,
                                damping: 40,
                            },
                        }}
                        className="bg-white absolute top-16 rounded-l border-black overflow-hidden w-full transition-opacity
                            shadow-custom z-[2]"
                    >
                        <motion.div ref={listRef} className="min-h-fit w-full relative">
                            <motion.div
                                style={{ opacity: loadingSpinnerOpacity }}
                                className="transform-center z-[1] transition-opacity"
                            >
                                <LoadingSpinner color="black" size={24} />
                            </motion.div>

                            <motion.div
                                style={{ opacity: resultsOpacity }}
                                className="w-full transition-opacity p-2"
                            >
                                {run(() => {
                                    const isEmpty = !cityOptions?.length

                                    if (isEmpty)
                                        return (
                                            <div className="flex items-center h-[73px] px-10 py-0 gap-x-4 bg-white relative w-full rounded-m">
                                                {t('citySearch.noResults')}
                                            </div>
                                        )

                                    return cityOptions?.map((option, index) => {
                                        if (index > MAX_SEARCH_RESULTS - 1) return null

                                        return (
                                            <CityOption
                                                highlighted={highlightedIndex === index}
                                                key={option.value.slug}
                                                option={option}
                                                onClick={() => onSelect(option)}
                                                setIsLoading={setIsLoading}
                                            />
                                        )
                                    })
                                })}
                            </motion.div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
